/**
 * The pharmacopoeia (M4-03) — what each herb is actually worth.
 *
 * Five plants, and every number in here is a modifier on something the body was
 * already doing. There is no entry that heals, none that closes a wound, and
 * none that skips a clock, because `HerbEffect` gives an entry no way to say so
 * — which is the ticket's design claim ("no red potions, nothing that breaks the
 * body's clocks") enforced by a type rather than by discipline.
 *
 * The ordering the ticket asks for, and what it costs to get:
 *
 * - **Yarrow** is the field herb: dice on the roll, and a nudge on the bleed. It
 *   grows on dry grass an hour from anywhere.
 * - **Honey** is the premium antiseptic — the best infection number here by
 *   some way — and the hive fights back for it.
 * - **Garlic** is the cheap one: a real cleaning bonus, grown in the village
 *   beds, and nothing to fear in the picking.
 * - **Sphagnum** is the standard dressing, honestly better than linen and
 *   perishable, so a wounded camp has to keep going back to the bog.
 * - **Willow** is the only thing here that touches *pain*, and it mends nothing
 *   at all. That is the point of it, and the reason it is the herb a player is
 *   most likely to misunderstand and then understand.
 *
 * The prose in `learned` is what a character reads once they have watched the
 * herb work. It is written as an observation rather than as a stat line for the
 * reason M3-03's examine text is: "the bleeding slows under it" is knowledge, and
 * "+1 die" is an item card.
 */
import { DAYS } from '@annwn/sim';
import { ITEM_KIT } from './flora.js';
/** A game hour, so the relief durations read as the hours they are. */
const HOURS = DAYS / 24;
export const HERBS = [
    /**
     * The warriors' woundwort. `+1` die is AoD §10.1's poultice modifier, kept
     * exactly; the bleed nudge is the ticket's "light standing bleed reduction",
     * and light is what it is — a third of one success.
     */
    {
        id: 'yarrow',
        dice: 1,
        bleed: 1,
        infection: 0.9,
        learned: 'Pressed into a wound it slows the bleeding — you have seen it happen.',
    },
    /**
     * The premium antiseptic. Half, which is the same factor a wash of it buys —
     * honey on a dressing is honey on a dressing, whichever verb reached for it.
     */
    {
        id: 'honey',
        infection: 0.5,
        learned: 'Spread on a dressing it keeps a wound sweet where nothing else will.',
    },
    /** Cheap, village-grown, and it genuinely helps the washing. */
    {
        id: 'garlic',
        dice: 1,
        infection: 0.7,
        learned: 'Crushed into the water it takes the sting of rot out of a wound.',
    },
    /** Better than linen, and it wilts. Both halves are the mechanic. */
    {
        id: 'sphagnum',
        bleed: 1,
        infection: 0.85,
        learned: 'It drinks up what a wound gives, and sours far slower than cloth.',
    },
    /**
     * Chewed. Pain only — no bleed, no infection, no wound state — which is the
     * distinction the acceptance criterion is written to catch.
     */
    {
        id: 'willow-bark',
        pain: 2,
        relief: HOURS * 3,
        learned: 'Bitter to chew, and the ache goes quiet for a few hours. It mends nothing.',
    },
    /* ------------------------------------------------------------------------ *
     * Prepared, at a fire
     * ------------------------------------------------------------------------ */
    /**
     * Yarrow crushed with water and worked into cloth. Worth a die more than the
     * leaf and a real reduction in the fever risk — an evening at the fire, bought
     * back the next time somebody opens an artery.
     */
    {
        id: 'yarrow-poultice',
        dice: 2,
        bleed: 2,
        infection: 0.75,
        learned: 'Bound on, it holds a wound shut better than anything you can pick whole.',
    },
    /**
     * Willow boiled down. The same pain relief as the bark, twice as long, and the
     * one thing in the game that helps a fevered body fight its own infection —
     * `fever` adds to the daily toughness roll (M1-05), which buys time rather
     * than a cure.
     */
    {
        id: 'willow-brew',
        pain: 2,
        fever: 3,
        relief: HOURS * 6,
        learned: 'Drunk hot, it dulls the pain and takes the edge off a fever for half a day.',
    },
];
const HERB_INDEX = new Map(HERBS.map((effect) => [effect.id, effect]));
export const HERB_KIT = HERB_INDEX;
export function herb(id) {
    const found = HERB_INDEX.get(id);
    if (!found)
        throw new Error(`no herb effect "${id}"`);
    return found;
}
/* -------------------------------------------------------------------------- *
 * What a fire makes
 * -------------------------------------------------------------------------- */
/**
 * The two preparations the MVP has, and they are the ticket's two.
 *
 * A poultice takes cloth as well as the herb, which is what makes linen worth
 * carrying for a reason other than binding; a brew takes only the bark and the
 * boiling water the fire is standing in for. Neither has a UI: the verb finds
 * the first of these the satchel can satisfy, in this order, and makes it.
 */
export const PREPARATIONS = [
    {
        id: 'yarrow-poultice',
        herbId: 'yarrow',
        withId: 'linen',
        line: 'crushes yarrow with water and works it into a strip of linen.',
    },
    /**
     * The same poultice, backed with moss instead of cloth.
     *
     * Second in the order so that somebody carrying both spends the linen — the
     * cloth is the *proper* backing and the moss is what the bog will give you for
     * free. It exists because of the ticket's last acceptance criterion: a full
     * herbal kit has to be assemblable **purely by foraging**, and linen is bought.
     * Without this line the best dressing in the game would be gated behind a
     * purchase, which is the opposite of what the valley is for.
     */
    {
        id: 'yarrow-poultice',
        herbId: 'yarrow',
        withId: 'sphagnum',
        line: 'crushes yarrow with water and beds it into a wad of bog moss.',
    },
    {
        id: 'willow-brew',
        herbId: 'willow-bark',
        withId: null,
        line: 'sets willow bark to boil, and the smell of it turns the stomach.',
    },
];
/**
 * Every id these tables name, checked at load — `checkFlora`'s rule for the same
 * reason it has one.
 *
 * A herb entry for an item that does not exist is a modifier no verb can ever
 * find, and a preparation naming a missing ingredient is an affordance that is
 * silently never offered. Both would look like a bug in the medicine verbs
 * rather than a typo in a table two packages away.
 */
function checkHerbs() {
    for (const effect of HERBS) {
        if (!ITEM_KIT.has(effect.id)) {
            throw new Error(`herb effect "${effect.id}" describes an item that does not exist`);
        }
    }
    for (const preparation of PREPARATIONS) {
        for (const id of [preparation.id, preparation.herbId, preparation.withId]) {
            if (id !== null && !ITEM_KIT.has(id)) {
                throw new Error(`preparation "${preparation.id}" names "${id}", which is not an item`);
            }
        }
        // A preparation whose result is worth nothing is an evening at the fire
        // spent on a downgrade — the one mistake this table can make silently.
        if (!HERB_INDEX.has(preparation.id)) {
            throw new Error(`preparation "${preparation.id}" produces something with no effect`);
        }
    }
}
checkHerbs();
//# sourceMappingURL=herbs.js.map